import React, { useMemo, useRef, useState } from 'react'
import { ReactSVG } from 'react-svg'
import { Select, View, Icon, TextInput, Text, Touchable } from '@/components'
import { AppIcon } from '@/config'
import { onUpdate } from '@codeleap/hooks'
import { arePropsEqual } from '@codeleap/utils'
import { TypeGuards } from '@codeleap/types'
import { usePhoneOptions } from '@/hooks'
import { FlagProps, PhoneSelectorProps, PhoneSelectorItemProps, PhoneInputProps, PhoneOption } from './types'
import { createStyles } from '@codeleap/styles'
import { useLingui } from '@lingui/react/macro'

function Flag(props: FlagProps) {
  const { flag, code, icon } = props

  return (
    <View style={styles.wrapperFlag}>
      <View style={styles.innerWrapperFlag}>
        {icon ? (
          <Icon debugName={'PhoneInput phone icon'} name={icon} style={['size:3', styles.icon]} />
        ) : (
          <img style={styles.flag} src={flag} />
        )}
      </View>

      <Text text={code} style={['p1', 'color:neutralSolid1000', 'marginLeft:1']} />
    </View>
  )
}

const PhoneSelector = (props: PhoneSelectorProps) => {
  const { isOpen, setIsOpen, options = [], selectedPhone, setSelectedPhone, selectRef, ...rest } = props

  const { t } = useLingui()

  return <Select
    options={options}
    debugName='Phone number input selector'
    title={t`Select phone`}
    placeholder={t`Search country`}
    value={selectedPhone}
    onValueChange={setSelectedPhone}
    searchable
    selectRef={selectRef}
    onBlur={() => setIsOpen(false)}
    {...rest}
  />
}

const PhoneSelectorItem = React.memo<PhoneSelectorItemProps>(({ data, isSelected }) => {
  return (
    <View style={[styles.itemWrapper, isSelected && styles['itemWrapper:selected']]}>
      <View style={styles.innerWrapper}>
        <View style={styles.innerWrapperFlag}>
          <ReactSVG style={styles.flag} src={data.flag} />
        </View>
        <Text text={data.code} style={['p1', 'color:neutralSolid1000', 'marginLeft:1']} />
        <Text text={data.description} style={['marginLeft:0.5', isSelected && 'color:neutralSolid1000']} />
      </View>
      {isSelected ? <Icon debugName={'PhoneInput check'} name='check' style={['size:2', 'color:textHeadingRegular']} /> : null}
    </View>
  )
}, (prev, next) => arePropsEqual(prev, next, { check: ['isSelected'] }))

const removeNonNumericCharsRegex = /\D/g

export const PhoneInput = (props: PhoneInputProps) => {
  const {
    value,
    onValueChange: providedOnValueChange,
    phone = null,
    selectProps = {},
    defaultLocale,
    disableInternalization,
    initialLocale,
    disableValidatorPhone = false,
    isPhoneValid,
    setPhoneValid = null,
    countrySelectable = true,
    ...rest
  } = props

  const { t } = useLingui()

  const { options, phones, localePhone } = usePhoneOptions({
    defaultLocale,
    disableInternalization,
    initialLocale,
  })

  const [selectedPhone, setSelectedPhone] = useState(localePhone?.value)
  const [isOpen, setIsOpen] = useState(false)
  const selectRef = useRef(null)

  const isPhoneValidOrInputOpen = isPhoneValid || (!isPhoneValid && isOpen)

  const hasPhone = !TypeGuards.isNil(phone)

  const currentPhone = React.useMemo(() => {
    if (hasPhone) return phone
    return phones?.[selectedPhone]
  }, [selectedPhone])

  const validatePhone = (value: string) => {
    if (!disableValidatorPhone) return { valid: true, message: null }

    const numericValue = value.replace(removeNonNumericCharsRegex, '')
    const currentPhoneNumericValue = currentPhone?.mask?.replace(removeNonNumericCharsRegex, '')
    const match = (numericValue.length >= currentPhoneNumericValue?.length)
    const invalidPhoneMessage = !match ? t`Invalid phone number` : ''

    if (setPhoneValid) setPhoneValid(match)

    return {
      valid: match,
      message: invalidPhoneMessage,
    }
  }

  const validation = useMemo(() => validatePhone(value), [value])

  const onValueChange = (value: string) => {
    validatePhone(value)
    providedOnValueChange(value)
  }

  const toggle = () => {
    setIsOpen(!isOpen)
  }

  const currentFlag = useMemo(() => {
    return (
      <Touchable debugName='phoneInput:flag' onPress={toggle}>
        <Flag flag={currentPhone?.flag} icon={(!countrySelectable && 'phone') as AppIcon} code={currentPhone?.code} />
      </Touchable>
    )
  }, [currentPhone?.flag])

  onUpdate(() => {
    if (isOpen && selectRef.current) {
      selectRef.current.focus()
    }
  }, [isOpen])

  return <>
    <TextInput
      label={t`Phone number`}
      description={countrySelectable && currentPhone?.description}
      placeholder={currentPhone?.maskPlaceholder}
      debugName={'Phone number input'}
      value={value}
      onValueChange={onValueChange}
      leftIcon={currentFlag as any}
      // @ts-ignore
      rightIcon={(!hasPhone && countrySelectable) && {
        name: 'chevron-down',
        onPress: toggle,
      }}
      masking={{
        type: 'custom',
        options: {
          mask: currentPhone?.applyMask,
        },
      }}
      forceError={validation?.message}
      // @ts-ignore
      style={[isPhoneValidOrInputOpen ? 'noError' : '', styles.input]}
      {...rest}
    />

    <View style={{ width: '100%', display: isOpen && countrySelectable ? 'flex' : 'none' }}>
      <PhoneSelector
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        selectRef={selectRef}
        selectedPhone={selectedPhone}
        setSelectedPhone={(value) => {
          if (!value) return
          setSelectedPhone(value)
          setIsOpen(false)
          onValueChange('')
        }}
        // @ts-ignore
        options={options}
        loadInitialValue
        backspaceRemovesValue={false}
        escapeRemoves={false}
        deleteRemoves={false}
        style={[isPhoneValidOrInputOpen && 'marginTop:1', { wrapper: styles.wrapper, innerWrapper: styles.innerWrapper }]}
        renderItem={(option: PhoneOption) => {
          return <PhoneSelectorItem isSelected={option?.isSelected} data={option?.data} />
        }}
        {...selectProps}
      />
    </View>
  </>
}

const styles = createStyles(theme => ({
  wrapper: {
    ...theme.presets.fullWidth,
  },
  wrapperFlag: {
    width: 'auto',
    height: theme.size.large - theme.spacing.value(1),
    ...theme.presets.center,
    ...theme.presets.row,
  },
  itemWrapper: {
    height: theme.size.large - theme.spacing.value(1),
    borderRadius: theme.radius.small,
    cursor: 'pointer',
    ...theme.presets.fullWidth,
    ...theme.presets.center,
    ...theme.presets.row,
    ...theme.spacing.paddingHorizontal(1),
  },
  'itemWrapper:selected': {
    backgroundColor: theme.baseColors.primarySolid300,
  },
  innerWrapperFlag: {
    ...theme.presets.center,
    width: theme.size.large - theme.spacing.value(2),
    height: theme.size.large - theme.spacing.value(3),
  },
  flag: {
    width: '100%',
    ...theme.spacing.marginTop(0.5),
  },
  icon: {
    color: theme.baseColors.neutralSolid600,
    fill: theme.baseColors.neutralSolid600,
  },
  input: {
    ...theme.spacing.marginLeft(0.5),
  },
  innerWrapper: {
    ...theme.presets.fullWidth,
  },
}))
